'use client';

/**
 * Single-flight access-token refresh, and the one place that decides whether a
 * failed refresh is survivable.
 *
 * Two rules govern everything here:
 *
 * 1. **A refresh is only ever terminal when Supabase positively says the
 *    refresh token is unusable.** Network failures, 429s and 5xx are transient;
 *    an unrecognized error is transient too; "there was no session" is its own
 *    outcome, because nothing died. Anything else logs a working user out,
 *    which is the defect this module exists to end.
 *
 * 2. **Never re-present a refresh token captured before an earlier attempt.**
 *    `refreshSession()` is always called with NO argument so it re-reads the
 *    current token from storage under the client's lock. The project has
 *    refresh-token reuse detection ON with a 10s reuse interval: replaying a
 *    token that the server already rotated — the exact shape of "the request
 *    landed but the response was lost" — makes Supabase revoke the entire token
 *    family and sign the user out of every device. A retry carrying a stale
 *    token would be a strictly worse bug than the one being fixed.
 *    (`planning/auth-session-expiry/plan.md` §3.)
 */

import {
  AuthSessionMissingError,
  isAuthApiError,
  isAuthRetryableFetchError,
  type AuthError,
} from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import {
  AuthSessionEvent,
  logAuthSessionEvent,
} from '@/lib/authSessionTelemetry';
import { AuthRefreshOutcome } from '@/types/auth-types';
import { LogLevel } from '@cabal/shared/util/seq-log';

interface AuthRefreshResultLocal {
  outcome: AuthRefreshOutcome;
  accessToken: string | null;
  /** Error code / class name, for telemetry only. */
  reason: string | null;
}

/**
 * Supabase auth error codes that mean the refresh token itself is unusable.
 * Nothing outside this set may end a session.
 */
enum TerminalAuthErrorCode {
  RefreshTokenNotFound = 'refresh_token_not_found',
  RefreshTokenAlreadyUsed = 'refresh_token_already_used',
  SessionExpired = 'session_expired',
}

enum AuthRefreshReason {
  NoSessionReturned = 'no_session_returned',
  SessionMissing = 'session_missing',
  Unknown = 'unknown',
  Unexpected = 'unexpected',
}

const TERMINAL_AUTH_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  TerminalAuthErrorCode.RefreshTokenNotFound,
  TerminalAuthErrorCode.RefreshTokenAlreadyUsed,
  TerminalAuthErrorCode.SessionExpired,
]);

const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
const HTTP_STATUS_SERVER_ERROR_MIN = 500;

let pendingRefresh: Promise<AuthRefreshResultLocal> | null = null;

/**
 * When a refresh last reached `auth-js`, from ANY caller.
 *
 * Every failed refresh populates a 60s failure cache keyed by refresh token
 * (`GoTrueClient.js:4292-4296`), so an attempt made inside that window is
 * answered from the cache without touching the network. Tracking the timestamp
 * here — the single funnel — is what lets the recovery ladder avoid spending a
 * step on a cached failure it did not itself cause.
 */
let lastRefreshAttemptAtMs = 0;

export function getLastRefreshAttemptAtMs(): number {
  return lastRefreshAttemptAtMs;
}

/**
 * Record a refresh that `auth-js` performed on our behalf rather than through
 * `refreshAccessToken` — notably the one `getSession()` runs internally at auth
 * initialization. It populates the same failure cache, so the ladder has to
 * know about it.
 */
export function noteExternalRefreshAttempt(): void {
  lastRefreshAttemptAtMs = Date.now();
}

/**
 * Classify an auth error raised by ANY refresh, including one `auth-js`
 * performed internally (notably inside `getSession()` at auth initialization).
 *
 * Exported so callers outside this module classify with the same rules instead
 * of re-deriving which error codes mean "the refresh token is dead" — getting
 * that wrong in a second place is how a genuinely dead session slips past the
 * forced-sign-out owner and never reaches the re-auth flow.
 */
export function classifyAuthRefreshError(error: AuthError): {
  outcome: AuthRefreshOutcome;
  reason: string;
} {
  return {
    outcome: classifyRefreshError(error),
    reason: readErrorReason(error),
  };
}

function classifyRefreshError(error: AuthError): AuthRefreshOutcome {
  // Network drop, aborted fetch, and the 5xx family all arrive here.
  if (isAuthRetryableFetchError(error)) {
    return AuthRefreshOutcome.Transient;
  }

  // No session in storage at all. NOT terminal: nothing died, the app is simply
  // already signed out — a signed-out visitor collecting 401s on private routes
  // reaches exactly this branch, and treating it as a dead session would sign
  // out someone who was never signed in.
  if (error instanceof AuthSessionMissingError) {
    return AuthRefreshOutcome.NoSession;
  }

  if (isAuthApiError(error)) {
    if (error.code && TERMINAL_AUTH_ERROR_CODES.has(error.code)) {
      return AuthRefreshOutcome.Terminal;
    }
    if (
      error.status === HTTP_STATUS_TOO_MANY_REQUESTS ||
      error.status >= HTTP_STATUS_SERVER_ERROR_MIN
    ) {
      return AuthRefreshOutcome.Transient;
    }
  }

  // Deliberately transient: an unrecognized failure leaves the user signed in
  // and retrying. The cost of being wrong here is a tab that recovers on the
  // next attempt or on reload; the cost of guessing "terminal" is a working
  // user thrown back to the sign-in screen.
  return AuthRefreshOutcome.Transient;
}

function readErrorReason(error: AuthError): string {
  if (isAuthApiError(error) && error.code) {
    return error.code;
  }
  return error.name || AuthRefreshReason.Unknown;
}

async function performRefresh(): Promise<AuthRefreshResultLocal> {
  lastRefreshAttemptAtMs = Date.now();
  try {
    // NO argument — see rule 2 in the module header. This re-reads the current
    // refresh token from storage under the client lock on every attempt.
    const { data, error } = await supabase.auth.refreshSession();

    if (error) {
      return {
        outcome: classifyRefreshError(error),
        accessToken: null,
        reason: readErrorReason(error),
      };
    }

    if (!data.session) {
      return {
        outcome: AuthRefreshOutcome.NoSession,
        accessToken: null,
        reason: AuthRefreshReason.NoSessionReturned,
      };
    }

    return {
      outcome: AuthRefreshOutcome.Refreshed,
      accessToken: data.session.access_token,
      reason: null,
    };
  } catch (error) {
    // `refreshSession` throws rather than returning when the session slot is
    // empty; everything else that escapes is treated as survivable.
    if (error instanceof AuthSessionMissingError) {
      return {
        outcome: AuthRefreshOutcome.NoSession,
        accessToken: null,
        reason: AuthRefreshReason.SessionMissing,
      };
    }
    return {
      outcome: AuthRefreshOutcome.Transient,
      accessToken: null,
      reason: AuthRefreshReason.Unexpected,
    };
  }
}

/**
 * Refresh the access token, collapsing concurrent callers onto one attempt.
 *
 * Six pollers and the recovery ladder can all want a refresh in the same tick;
 * without this gate they would each fire their own `/token` call. `auth-js`
 * dedupes in-flight refreshes internally too, but the local gate also keeps the
 * telemetry honest about how many refreshes actually happened.
 */
export function refreshAccessToken(): Promise<AuthRefreshResultLocal> {
  if (pendingRefresh) {
    return pendingRefresh;
  }

  const attempt = performRefresh()
    .then((result) => {
      logAuthSessionEvent({
        event: AuthSessionEvent.TokenRefreshOutcome,
        props: { outcome: result.outcome, reason: result.reason },
        level:
          result.outcome === AuthRefreshOutcome.Refreshed
            ? LogLevel.Info
            : LogLevel.Warning,
      });
      return result;
    })
    .finally(() => {
      pendingRefresh = null;
    });

  pendingRefresh = attempt;
  return attempt;
}
